import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Moon, Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/theme";
import { useI18n } from "@/lib/i18n";
import { BRAND_LOGO_URL, NAV_HASHES, LANGS } from "@/data/landing";

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const otherLang = lang === "tr" ? "en" : "tr";

  return (
    <header
      data-testid="site-navbar"
      className={[
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "glass-light border-b border-neutral-200/60 dark:border-neutral-800/70"
          : "bg-transparent",
      ].join(" ")}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
          aria-label={t("brand.name")}
        >
          <div className="relative h-10 w-10 rounded-2xl overflow-hidden bg-neutral-950 ring-1 ring-neutral-200/60 dark:ring-neutral-800 transition-transform group-hover:-translate-y-0.5">
            <img
              src={BRAND_LOGO_URL}
              alt={t("brand.name") + " logo"}
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
              {t("brand.name")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
              {t("brand.kicker")}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_HASHES.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-emerald-800 dark:hover:text-emerald-400 transition-colors rounded-full"
            >
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <button
            type="button"
            data-testid="language-toggle"
            onClick={() => setLang(otherLang)}
            aria-label={`Switch language to ${LANGS[otherLang].name}`}
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60 text-xs font-bold uppercase tracking-[0.18em] hover:-translate-y-0.5 transition"
          >
            <Globe className="h-3.5 w-3.5 text-emerald-800 dark:text-emerald-400" />
            <span className="text-neutral-800 dark:text-neutral-100">
              {LANGS[lang].label}
              <span className="mx-1 text-neutral-400">/</span>
              <span className="text-neutral-400">{LANGS[otherLang].label}</span>
            </span>
          </button>

          <button
            type="button"
            data-testid="theme-toggle-button"
            onClick={toggle}
            aria-label={theme === "dark" ? t("nav.lightAria") : t("nav.darkAria")}
            className="h-10 w-10 rounded-full inline-flex items-center justify-center border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60 hover:-translate-y-0.5 transition"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4 text-amber-400" />
            ) : (
              <Moon className="h-4 w-4 text-neutral-700" />
            )}
          </button>

          <Button
            asChild
            data-testid="navbar-cta-download"
            className="hidden sm:inline-flex h-10 rounded-full px-5 font-semibold bg-emerald-800 hover:bg-emerald-900 text-stone-50 hover:text-white pill-shadow hover:-translate-y-0.5 transition"
          >
            <a href="#download">{t("nav.cta")}</a>
          </Button>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
            className="lg:hidden h-10 w-10 rounded-full inline-flex items-center justify-center border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/60"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden glass-light border-b border-neutral-200/60 dark:border-neutral-800/70"
        >
          <nav className="px-5 py-4 flex flex-col gap-1" aria-label="Mobile">
            {NAV_HASHES.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.key}`}
                className="px-4 py-3 text-base font-medium text-neutral-800 dark:text-neutral-200 hover:bg-stone-100 dark:hover:bg-neutral-800/60 rounded-2xl"
              >
                {t(`nav.${l.key}`)}
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                setLang(otherLang);
                setOpen(false);
              }}
              data-testid="mobile-language-toggle"
              className="mt-1 px-4 py-3 inline-flex items-center gap-2 rounded-2xl border border-neutral-200 dark:border-neutral-800 text-sm font-semibold"
            >
              <Globe className="h-4 w-4 text-emerald-800 dark:text-emerald-400" />
              {LANGS[lang].name}
              <span className="text-neutral-400">→ {LANGS[otherLang].name}</span>
            </button>
            <a
              href="#download"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="mt-2 px-5 py-3 text-center rounded-full bg-emerald-800 text-stone-50 font-semibold"
            >
              {t("nav.cta")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
